// Host detail (spec §6.4): pay-rail history with freeze, incident playbook
// (freeze every rail + snapshot recent payers), full-prepay unlock/lock,
// strikes, photos (TASKS 115), session revoke.
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { adminMutate } from '@/lib/api/adminMutate';
import { toReason } from '@/lib/api/reason';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { HostPhotos } from '@/lib/admin/HostPhotos';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/hosts/$id')({
  component: HostDetail,
});

interface Rail {
  id: string;
  provider: string;
  kind: string;
  status: string;
  account_holder: string | null;
  account_number: string | null;
  frozen_at: string | null;
  frozen_reason: string | null;
  created_at: string;
}

interface Incident {
  id: string;
  reason: string;
  window_start: string;
  created_at: string;
  rail_incident_payers: Array<{ id: string; amount_nad: number; notified_at: string | null; outcome: string }>;
}

interface Strike {
  id: string;
  booking_id: string | null;
  kind: string;
  reason: string;
  created_at: string;
  host_strike_revocations: Array<{ id: string; reason: string; created_at: string }> | null;
}

interface SettlementOverview {
  business: { id: string; name: string | null; owner_id: string; created_at: string; full_prepay_unlocked: boolean };
  fullPrepay: { unlocked: boolean; eligible: boolean; reasons?: string[] };
  rails: Rail[];
  incidents: Incident[];
  strikes: Strike[];
}

interface Session {
  id: string;
  created_at: string;
  not_after: string | null;
  aal: string | null;
  user_agent: string | null;
  ip: string | null;
}

function useOverview(businessId: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-settlement', 'overview', businessId],
    queryFn: () => callEdgeFunction<SettlementOverview>(supabase, 'admin-settlement', { action: 'overview', businessId }),
  });
}

function HostDetail() {
  const { id } = Route.useParams();
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useOverview(id);

  const [reason, setReason] = useState('');
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const invalidate = () => cancelThenInvalidate(queryClient, { queryKey: ['admin-settlement', 'overview', id] });

  const withReason = <T,>(fn: (r: ReturnType<typeof toReason>) => Promise<T>) => async () => {
    const r = toReason(reason);
    if (!r) throw new Error('A reason of at least 10 characters is required.');
    const result = await fn(r);
    setReason('');
    invalidate();
    return result;
  };

  const freezeRailMutation = useMutation({
    mutationFn: (railId: string) =>
      withReason((r) => adminMutate(supabase, 'admin-settlement', { action: 'freeze_rail', railId }, r!))(),
  });

  // Controller note (task 22 fix round): the incident/freeze button must go
  // through open_incident (freezes every rail AND snapshots recent payers
  // for notification), not the single-rail freeze_rail above — freeze_rail
  // stays available per-rail for a targeted freeze that isn't a platform
  // incident.
  const openIncidentMutation = useMutation({
    mutationFn: withReason((r) => adminMutate(supabase, 'admin-settlement', { action: 'open_incident', businessId: id }, r!)),
  });

  const markNotifiedMutation = useMutation({
    mutationFn: (incidentId: string) =>
      withReason((r) => adminMutate(supabase, 'admin-settlement', { action: 'mark_incident_notified', incidentId }, r!))(),
  });

  const fullPrepayMutation = useMutation({
    mutationFn: (unlock: boolean) =>
      withReason((r) =>
        adminMutate(supabase, 'admin-settlement', { action: unlock ? 'unlock_full_prepay' : 'lock_full_prepay', businessId: id }, r!),
      )(),
  });

  const strikeMutation = useMutation({
    mutationFn: withReason((r) => adminMutate(supabase, 'admin-strike', { action: 'add', businessId: id }, r!)),
  });

  const removeStrikeMutation = useMutation({
    mutationFn: (strikeId: string) =>
      withReason((r) => adminMutate(supabase, 'admin-strike', { action: 'remove', strikeId }, r!))(),
  });

  const sessionsQuery = useQuery({
    queryKey: ['admin-revoke-sessions', 'list', data?.business.owner_id],
    queryFn: () => callEdgeFunction<{ sessions: Session[] }>(supabase, 'admin-revoke-sessions', { action: 'list', userId: data!.business.owner_id }),
    enabled: sessionsOpen && !!data?.business.owner_id,
  });

  const revokeSessionsMutation = useMutation({
    mutationFn: withReason((r) =>
      adminMutate(supabase, 'admin-revoke-sessions', { action: 'revoke', userId: data!.business.owner_id }, r!),
    ),
  });

  if (isLoading) return <PageLoading />;
  if (error || !data) return <p className="text-body text-danger">Couldn't load this host: {(error as Error)?.message}</p>;

  const { business, fullPrepay, rails, incidents, strikes } = data;
  const anyMutationPending =
    freezeRailMutation.isPending || openIncidentMutation.isPending || markNotifiedMutation.isPending ||
    fullPrepayMutation.isPending || strikeMutation.isPending || removeStrikeMutation.isPending ||
    revokeSessionsMutation.isPending;

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">{business.name ?? 'Unnamed business'}</h1>
        <p className="mt-xs text-body text-ink-2">
          Full prepay: {fullPrepay.unlocked ? 'unlocked' : 'locked'}
          {!fullPrepay.unlocked && fullPrepay.eligible ? ' · eligible to unlock' : ''}
        </p>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <label className="text-caption font-semibold text-ink-2" htmlFor="host-reason">
          Reason (required for every action below)
        </label>
        <textarea
          id="host-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-xs min-h-[52px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
        />
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Pay rails</h2>
        <ul className="mt-md flex flex-col gap-sm">
          {rails.map((rail) => (
            <li key={rail.id} className="flex flex-wrap items-center justify-between gap-sm border-b border-hair pb-sm">
              <span className="text-body text-ink">
                {rail.provider} · {rail.kind} · {rail.status}
                {rail.account_number ? ` · ${rail.account_number}` : ''}
              </span>
              {rail.status !== 'frozen' ? (
                <button
                  type="button"
                  disabled={anyMutationPending || !toReason(reason)}
                  onClick={() => freezeRailMutation.mutate(rail.id)}
                  className="rounded-button border border-danger px-sm py-xs text-caption font-semibold text-danger disabled:opacity-50"
                >
                  Freeze this rail
                </button>
              ) : (
                <span className="text-caption text-danger">Frozen{rail.frozen_reason ? `: ${rail.frozen_reason}` : ''}</span>
              )}
            </li>
          ))}
          {rails.length === 0 ? <li className="text-body text-ink-2">No pay rails on record.</li> : null}
        </ul>
        {freezeRailMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(freezeRailMutation.error as Error).message}</p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <div className="flex items-center justify-between">
          <h2 className="text-h3 font-bold text-ink">Incident playbook</h2>
          <button
            type="button"
            disabled={anyMutationPending || !toReason(reason)}
            onClick={() => openIncidentMutation.mutate()}
            className="rounded-button bg-danger px-md py-sm text-body font-semibold text-white disabled:opacity-50"
          >
            Open incident (freeze all rails)
          </button>
        </div>
        <p className="mt-xs text-caption text-ink-2">
          Freezes every rail on this business and snapshots recent payers to notify.
        </p>
        <ul className="mt-md flex flex-col gap-sm">
          {incidents.map((inc) => (
            <li key={inc.id} className="rounded-button border border-border p-sm">
              <p className="text-body text-ink">{inc.reason}</p>
              <p className="text-caption text-ink-2">
                {inc.rail_incident_payers.length} payer(s) since {new Date(inc.window_start).toLocaleDateString()} ·{' '}
                {inc.rail_incident_payers.filter((p) => !p.notified_at).length} not yet notified
              </p>
              {inc.rail_incident_payers.some((p) => !p.notified_at) ? (
                <button
                  type="button"
                  disabled={anyMutationPending || !toReason(reason)}
                  onClick={() => markNotifiedMutation.mutate(inc.id)}
                  className="mt-xs text-caption font-semibold text-canyon underline"
                >
                  Mark notified
                </button>
              ) : null}
            </li>
          ))}
          {incidents.length === 0 ? <li className="text-body text-ink-2">No incidents on record.</li> : null}
        </ul>
        {openIncidentMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(openIncidentMutation.error as Error).message}</p>
        ) : null}
        {markNotifiedMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(markNotifiedMutation.error as Error).message}</p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Full prepay</h2>
        <button
          type="button"
          disabled={anyMutationPending || !toReason(reason)}
          onClick={() => fullPrepayMutation.mutate(!fullPrepay.unlocked)}
          className="mt-sm rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
        >
          {fullPrepay.unlocked ? 'Lock full prepay' : 'Unlock full prepay'}
        </button>
        {fullPrepayMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(fullPrepayMutation.error as Error).message}</p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Photos</h2>
        <p className="mt-xs text-caption text-ink-2">
          Remove a photo, choose the cover, or add one. A removed photo uploaded to NamibiaGo is deleted from storage.
        </p>
        <div className="mt-md">
          <HostPhotos businessId={id} reason={toReason(reason)} onEdited={() => setReason('')} />
        </div>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Strikes</h2>
        <ul className="mt-sm flex flex-col gap-sm">
          {strikes.map((s) => {
            const revocation = s.host_strike_revocations?.[0] ?? null;
            return (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-sm border-b border-hair pb-sm">
                <div>
                  <p className="text-body text-ink">
                    {s.kind} — {s.reason}
                  </p>
                  <p className="text-caption text-ink-2">
                    {new Date(s.created_at).toLocaleDateString()}
                    {revocation ? ` · revoked: ${revocation.reason} (${new Date(revocation.created_at).toLocaleDateString()})` : ''}
                  </p>
                </div>
                {!revocation ? (
                  <button
                    type="button"
                    disabled={anyMutationPending || !toReason(reason)}
                    onClick={() => removeStrikeMutation.mutate(s.id)}
                    className="rounded-button border border-border px-sm py-xs text-caption font-semibold text-ink disabled:opacity-50"
                  >
                    Remove
                  </button>
                ) : (
                  <span className="text-caption text-ink-2">Revoked</span>
                )}
              </li>
            );
          })}
          {strikes.length === 0 ? <li className="text-body text-ink-2">No strikes on record.</li> : null}
        </ul>
        <button
          type="button"
          disabled={anyMutationPending || !toReason(reason)}
          onClick={() => strikeMutation.mutate()}
          className="mt-sm rounded-button border border-danger px-md py-sm text-body font-semibold text-danger disabled:opacity-50"
        >
          Add strike
        </button>
        {strikeMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(strikeMutation.error as Error).message}</p>
        ) : null}
        {removeStrikeMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(removeStrikeMutation.error as Error).message}</p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <div className="flex items-center justify-between">
          <h2 className="text-h3 font-bold text-ink">Sessions</h2>
          <button type="button" onClick={() => setSessionsOpen(true)} className="text-caption font-semibold text-canyon underline">
            Load sessions
          </button>
        </div>
        {sessionsOpen && sessionsQuery.data ? (
          <>
            <ul className="mt-sm flex flex-col gap-xs">
              {sessionsQuery.data.sessions.map((s) => (
                <li key={s.id} className="text-caption text-ink-2">
                  {s.aal ?? '—'} · {s.user_agent ?? '—'} · {s.ip ?? '—'} · since {new Date(s.created_at).toLocaleString()}
                </li>
              ))}
              {sessionsQuery.data.sessions.length === 0 ? <li className="text-caption text-ink-2">No active sessions.</li> : null}
            </ul>
            {sessionsQuery.data.sessions.length > 0 ? (
              <button
                type="button"
                disabled={anyMutationPending || !toReason(reason)}
                onClick={() => revokeSessionsMutation.mutate()}
                className="mt-sm rounded-button border border-danger px-md py-sm text-body font-semibold text-danger disabled:opacity-50"
              >
                Revoke all sessions
              </button>
            ) : null}
          </>
        ) : null}
        {sessionsQuery.isError ? (
          <p className="mt-sm text-body text-danger">Couldn't load sessions: {(sessionsQuery.error as Error).message}</p>
        ) : null}
        {revokeSessionsMutation.isError ? (
          <p className="mt-sm text-body text-danger">{(revokeSessionsMutation.error as Error).message}</p>
        ) : null}
      </div>
    </section>
  );
}
